import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap";
import "./App.css";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { useEffect, useState, Suspense, lazy } from "react";
import axiosInstance from './utils/axiosConfig';
import { API_URL } from "./Components/constants";
import 'react-toastify/dist/ReactToastify.css';
import { HelmetProvider } from 'react-helmet-async';
import { updateFavicon } from './utils/GeneralSettings';
import { setupNetworkListener } from './utils/networkDetector';
import { toast } from 'react-toastify';
import { logDiagnostics, testAPIConnectivity } from './utils/apiDiagnostics';
import "./Components/Navbar.css";

// Lazy load components for code splitting and better performance
const Header = lazy(() => import("./Components/Header"));
const Footer = lazy(() => import("./Components/Footer"));
const Home = lazy(() => import("./Components/Home"));
const Contact = lazy(() => import("./Components/Aboutus/Contact"));
const Aboutclinic = lazy(() => import("./Components/Aboutus/Aboutclinic"));
const Aboutdr = lazy(() => import("./Components/Aboutus/Aboutdr"));
const Reviews = lazy(() => import("./Components/PatientGuide/Reviews"));
const ImageLightbox = lazy(() => import("./Components/Images"));
const DynamicPage = lazy(() => import('./Components/Dynamicpage/Dynamicpage'));

function openNav() {
  document.getElementById("mySidebar").style.width = "250px";
  document.getElementById("main").style.marginLeft = "250px";
}

function closeNav() {
  document.getElementById("mySidebar").style.width = "0";
  document.getElementById("main").style.marginLeft = "0";
}

function App() {
  const [isMobile, setIsMobile] = useState(false);
  const [generalSettings, setGeneralSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  //choose the screen size
  const handleResize = () => {
    if (window.innerWidth < 1200) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    // Set initial mobile state
    handleResize();

    // Setup network connectivity listener
    const cleanupNetworkListener = setupNetworkListener(
      () => {
        // Network restored
        toast.success('Connection restored', { autoClose: 2000 });
      },
      () => {
        // Network lost
        toast.warning('No internet connection. Some features may not work.', { autoClose: 3000 });
      }
    );

    const fetchData = async () => {
      // Log diagnostics in development mode
      if (process.env.NODE_ENV === 'development') {
        logDiagnostics();
      }

      try {
        const [settingsResponse, categoriesResponse] = await Promise.all([
          axiosInstance.get(`general-settings`),
          axiosInstance.get(`get-dynamic-page-category`)
        ]);

        setGeneralSettings(settingsResponse.data);
        setCategories(categoriesResponse.data);
        localStorage.setItem('generalSettings', JSON.stringify(settingsResponse.data));

        // Update favicon dynamically
        if (settingsResponse.data.favicon) {
          updateFavicon(settingsResponse.data.favicon);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Network errors are already handled by axios interceptor
        // Try to use cached data if available
        const storedSettings = localStorage.getItem('generalSettings');
        if (storedSettings) {
          try {
            const settings = JSON.parse(storedSettings);
            setGeneralSettings(settings);
            // Update favicon dynamically from stored settings
            if (settings.favicon) {
              updateFavicon(settings.favicon);
            }
          } catch (parseError) {
            console.error('Error parsing stored settings:', parseError);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (cleanupNetworkListener) cleanupNetworkListener();
    };
  }, []);

  // Don't block initial render - show UI immediately with cached data
  // if (loading) {
  //   return <div>Loading...</div>;
  // }


  // Loading component for Suspense
  const LoadingFallback = () => (
    <div className="text-center p-5">
      <div>Loading...</div>
    </div>
  );

  return (
    <HelmetProvider>
      <div className="App">
        <Suspense fallback={<LoadingFallback />}>
          <Header generalSettings={generalSettings} />
        </Suspense>
        {isMobile ? (
          <>
            <div id="mySidebar" className="sidebar">
              <a href="javascript:void(0)" className="closebtn" onClick={closeNav}>×</a>
              <ul className="ul-nav1">
                <li className="nav-link">
                  <Link to="/">Home</Link>
                </li>

                {/* Dynamic Categories */}
                {categories.map((category) => (
                  <li className="nav-link" key={category.id}>
                    <a href="#">
                      {category.name}
                      <i className="fas fa-caret-down" />
                    </a>
                    <div className="dropdown">
                      <ul className="ul-nav2">
                        {category.pages?.map((page) => (
                          <li className="dropdown-link" key={page.id}>
                            <Link to={`/page/${page.slug}`}>
                              {page.page_name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}

                <li className="nav-link">
                  <Link to="/Reviews">Reviews</Link>
                </li>

                <li className="nav-link">
                  <a href="#">
                    About us
                    <i className="fas fa-caret-down" />
                  </a>
                  <div className="dropdown">
                    <ul className="ul-nav2">
                      <li className="dropdown-link">
                        <Link to="/Aboutdr">About Dr. Hema Krishna's Neurology</Link>
                      </li>
                      <li className="dropdown-link">
                        <Link to="/Contact">Contact us</Link>
                      </li>
                    </ul>
                  </div>
                </li>

                <li className="nav-link">
                  <Link to="/Gallery">Gallery</Link>
                </li>
              </ul>
            </div>
            <div id="main1">
              <button className="openbtn" onClick={openNav}>
                ☰ Dr. Hema Krishna's Neurology
              </button>
            </div>
          </>
        ) : (
          <div className="headerSEct">
            <div className="headerHome">
              <div className="">
                <div className="nav-btn">
                  <div className="nav-links">
                    <ul className="ul-nav1">
                      <li className="nav-link">
                        <Link to="/">
                          <i className="fa fa-home" style={{ fontSize: "20px" }}></i>
                        </Link>
                      </li>

                      {/* Dynamic Categories */}
                      {categories.map((category) => (
                        <li className="nav-link" key={category.id}>
                          <a href="#">
                            {category.name}
                            <i className="fas fa-caret-down" />
                          </a>
                          <div className="dropdown">
                            <ul className="ul-nav2">
                              {category.pages?.map((page) => {
                                return (
                                  <li className="dropdown-link" key={page.id}>
                                    <Link to={`/page/${page.slug}`}>
                                      {page.page_name}
                                    </Link>
                                  </li>
                                );
                              })}

                            </ul>
                          </div>
                        </li>
                      ))}

                      <li className="nav-link">
                        <Link to="/Reviews">Reviews</Link>
                      </li>

                      <li className="nav-link">
                        <a href="#">
                          About us
                          <i className="fas fa-caret-down" />
                        </a>
                        <div className="dropdown">
                          <ul className="ul-nav2">
                            <li className="dropdown-link">
                              <Link to="/Aboutdr">About Dr. Hema Krishna</Link>
                            </li>
                            <li className="dropdown-link">
                              <Link to="/Contact">Contact us</Link>
                            </li>
                          </ul>
                        </div>
                      </li>

                      <li className="nav-link">
                        <Link to="/Gallery">Gallery</Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route exact path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/Reviews" element={<Reviews />} />
            <Route path="/Aboutdr" element={<Aboutdr />} />
            <Route path="/Gallery" element={<ImageLightbox />} />
            <Route path="/Aboutclinic" element={<Aboutclinic />} />

            {/* Dynamic Routes */}
            <Route path="/page/:slug" element={<DynamicPage />} />

            {/* 404 Route */}
            <Route path="*" element={
              <div className="container my-5 text-center">
                <h1>404 - Page Not Found</h1>
                <p>The page you are looking for does not exist.</p>
              </div>
            } />
          </Routes>
        </Suspense>

        <Suspense fallback={<LoadingFallback />}>
          <Footer generalSettings={generalSettings} />
        </Suspense>
      </div>
    </HelmetProvider>
  );
}

export default App;
